import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import FieldHint from "../FieldHint";
import useI18n from "../../../i18n/useI18n";

import MotionSurface from "../../../motion/studio/MotionSurface";
import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";
import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";
import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const STUDIO_USAGE_REFRESH_KEY = "hookora_studio_usage_refresh";
const THEME_KEY = "hookora_theme";

const getPreferredLanguage = () => {
  if (typeof window === "undefined") return "en";
  return localStorage.getItem("hookora_language") || "en";
};

const notifyStudioUsageUpdated = () => {
  const value = String(Date.now());
  localStorage.setItem(STUDIO_USAGE_REFRESH_KEY, value);
  window.dispatchEvent(new CustomEvent("hookora:studio-usage-updated"));
};

const getScoreTone = (score) => {
  const safe = Number(score) || 0;
  if (safe >= 85) {
    return {
      bar: "#16a34a",
      pillBg: "rgba(22,163,74,0.14)",
      pillText: "#16a34a",
    };
  }
  if (safe >= 70) {
    return {
      bar: "#2563eb",
      pillBg: "rgba(37,99,235,0.14)",
      pillText: "#2563eb",
    };
  }
  return {
    bar: "#f59e0b",
    pillBg: "rgba(245,158,11,0.16)",
    pillText: "#b45309",
  };
};

const ThumbnailTool = ({ sharedTopic, setSharedTopic }) => {
  const { t } = useI18n();

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [thumbnailText, setThumbnailText] = useState("");
  const [audience, setAudience] = useState("");
  const [angle, setAngle] = useState("");
  const [desiredOutcome, setDesiredOutcome] = useState("");
  const [avoidPhrases, setAvoidPhrases] = useState("");
  const [extraContext, setExtraContext] = useState("");

  const [analysis, setAnalysis] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const syncTheme = () => setTheme(localStorage.getItem(THEME_KEY) || "light");
    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);
    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      cardBgStrong: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.22)" : "#e5e7eb",
      borderStrong: isDark ? "rgba(148,163,184,0.34)" : "#d1d5db",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputText: isDark ? "#f8fafc" : "#111827",
      trackBg: isDark ? "#1e293b" : "#e5e7eb",
      panelBg: isDark ? "rgba(30,41,59,0.55)" : "#f8fafc",
      buttonBg: isDark ? "#e2e8f0" : "#111827",
      buttonText: isDark ? "#020617" : "#ffffff",
      secondaryButtonBg: isDark ? "#1e293b" : "#e5e7eb",
      secondaryButtonText: isDark ? "#f8fafc" : "#111827",
      dangerBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      dangerText: isDark ? "#fecaca" : "#b91c1c",
      label: isDark ? "#e2e8f0" : "#111827",
      heroBg: isDark
        ? "linear-gradient(135deg, rgba(30,41,59,0.84), rgba(15,23,42,0.96))"
        : "linear-gradient(135deg, #f8fafc, #ffffff)",
      heroBorder: isDark ? "rgba(59,130,246,0.24)" : "#bfdbfe",
      heroBadgeBg: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
      heroBadgeText: isDark ? "#bfdbfe" : "#1d4ed8",
      heroBadgeBorder: isDark ? "rgba(96,165,250,0.24)" : "#bfdbfe",
      shadow: isDark
        ? "0 16px 34px rgba(2,6,23,0.34)"
        : "0 12px 28px rgba(15,23,42,0.06)",
      shadowSoft: isDark
        ? "0 10px 24px rgba(2,6,23,0.22)"
        : "0 6px 18px rgba(15,23,42,0.05)",
      metricsBg: isDark ? "rgba(30,41,59,0.88)" : "#f8fafc",
      metricsText: isDark ? "#cbd5e1" : "#475569",
    }),
    [isDark]
  );

  const inputStyle = {
    width: "100%",
    padding: "13px 14px",
    borderRadius: "12px",
    border: `1px solid ${colors.borderStrong}`,
    outline: "none",
    boxSizing: "border-box",
    background: colors.inputBg,
    color: colors.inputText,
    fontSize: "14px",
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: "86px",
    resize: "vertical",
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "6px",
    color: colors.label,
    fontSize: "14px",
    fontWeight: 600,
  };

  const clearResults = () => {
    setAnalysis(null);
    setSuggestions([]);
    setCopiedId(null);
    setError("");
  };

  const resetForm = () => {
    setSharedTopic("");
    setThumbnailText("");
    setAudience("");
    setAngle("");
    setDesiredOutcome("");
    setAvoidPhrases("");
    setExtraContext("");
    setAnalysis(null);
    setSuggestions([]);
    setCopiedId(null);
    setError("");
  };

  const analyzeText = async () => {
    if (!thumbnailText.trim() && !sharedTopic.trim()) {
      setError(
        t(
          "thumbnailTool.errors.enterTextOrTopic",
          "Enter thumbnail text or a topic first."
        )
      );
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await API.post("/studio/thumbnail/analyze", {
        topic: sharedTopic.trim(),
        thumbnail_text: thumbnailText,
        audience,
        angle,
        desired_outcome: desiredOutcome,
        avoid_phrases: avoidPhrases,
        extra_context: extraContext,
        language: getPreferredLanguage(),
      });

      setAnalysis(response?.data?.analysis || null);
      setSuggestions(Array.isArray(response?.data?.suggestions) ? response.data.suggestions : []);
      setCopiedId(null);
      notifyStudioUsageUpdated();
    } catch (err) {
      console.error("Failed to analyze thumbnail text:", err);
      setError(
        err?.response?.data?.detail ||
          t(
            "thumbnailTool.errors.analyzeFailed",
            "Failed to analyze thumbnail text."
          )
      );
    } finally {
      setLoading(false);
    }
  };

  const copyText = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy thumbnail text:", err);
    }
  };

  const renderScoreBar = (score) => {
    const safe = Number(score) || 0;
    const tone = getScoreTone(safe);
    return (
      <div
        style={{
          marginTop: "10px",
          height: "10px",
          borderRadius: "999px",
          background: colors.trackBg,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, safe))}%`,
            height: "100%",
            borderRadius: "999px",
            background: tone.bar,
          }}
        />
      </div>
    );
  };

  return (
    <MotionSurface>
    <div style={{ display: "grid", gap: "18px" }}>
      <div
        style={{
          border: `1px solid ${colors.heroBorder}`,
          borderRadius: "20px",
          padding: "18px",
          background: colors.heroBg,
          boxShadow: colors.shadow,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 10px",
            borderRadius: "999px",
            background: colors.heroBadgeBg,
            color: colors.heroBadgeText,
            border: `1px solid ${colors.heroBadgeBorder}`,
            fontSize: "12px",
            fontWeight: 800,
            letterSpacing: "0.03em",
            marginBottom: "12px",
          }}
        >
          {t("thumbnailTool.hero.badge", "CTR polish tool")}
        </div>

        <div style={{ color: colors.text, fontSize: "24px", fontWeight: 800, marginBottom: "8px" }}>
          {t("thumbnailTool.hero.title", "Thumbnail Text")}
        </div>

        <div style={{ color: colors.textSoft, lineHeight: "1.7", maxWidth: "880px" }}>
          {t(
            "thumbnailTool.hero.description",
            "Use this tool to analyze current thumbnail text and generate stronger alternatives. Add topic and positioning context so Hookora can suggest tighter, more clickable thumbnail words."
          )}
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${colors.border}`,
          borderRadius: "18px",
          padding: "18px",
          background: colors.cardBg,
          boxShadow: colors.shadowSoft,
        }}
      >
        <div style={{ display: "grid", gap: "14px" }}>
          <div>
            <label style={labelStyle}>
              {t("thumbnailTool.fields.topic.label", "Topic")}
              <FieldHint
                text={t(
                  "thumbnailTool.fields.topic.hint",
                  "Optional topic context for the thumbnail analyzer. This helps the suggestions feel more relevant to the content."
                )}
              />
            </label>
            <textarea
              value={sharedTopic}
              onChange={(e) => setSharedTopic(e.target.value)}
              placeholder={t(
                "thumbnailTool.fields.topic.placeholder",
                "Optional topic for the thumbnail analysis"
              )}
              style={textareaStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>
              {t("thumbnailTool.fields.thumbnailText.label", "Thumbnail Text")}
              <FieldHint
                text={t(
                  "thumbnailTool.fields.thumbnailText.hint",
                  "The exact thumbnail words you want analyzed. Keep it short and punchy for best results."
                )}
              />
            </label>
            <input
              value={thumbnailText}
              onChange={(e) => setThumbnailText(e.target.value)}
              placeholder={t(
                "thumbnailTool.fields.thumbnailText.placeholder",
                "Enter current thumbnail text"
              )}
              style={inputStyle}
            />
          </div>

          <div
            style={{
              padding: "14px",
              borderRadius: "14px",
              background: colors.panelBg,
              border: `1px solid ${colors.border}`,
            }}
          >
            <div style={{ fontWeight: 700, color: colors.text, marginBottom: "12px" }}>
              {t("thumbnailTool.analysisContext.title", "Analysis Context")}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "12px",
              }}
            >
              <div>
                <label style={labelStyle}>
                  {t("thumbnailTool.fields.audience.label", "Audience")}
                  <FieldHint
                    text={t(
                      "thumbnailTool.fields.audience.hint",
                      "Who the thumbnail is trying to attract. Example: creators, students, founders, fitness lovers."
                    )}
                  />
                </label>
                <input
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder={t("thumbnailTool.fields.audience.placeholder", "Audience")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("thumbnailTool.fields.angle.label", "Angle")}
                  <FieldHint
                    text={t(
                      "thumbnailTool.fields.angle.hint",
                      "The positioning or emotional framing. Example: shocking, premium, direct, urgent, beginner-friendly."
                    )}
                  />
                </label>
                <input
                  value={angle}
                  onChange={(e) => setAngle(e.target.value)}
                  placeholder={t("thumbnailTool.fields.angle.placeholder", "Angle")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("thumbnailTool.fields.desiredOutcome.label", "Desired Outcome")}
                  <FieldHint
                    text={t(
                      "thumbnailTool.fields.desiredOutcome.hint",
                      "What result the thumbnail should help drive. Example: clicks, curiosity, watch time, conversions."
                    )}
                  />
                </label>
                <input
                  value={desiredOutcome}
                  onChange={(e) => setDesiredOutcome(e.target.value)}
                  placeholder={t(
                    "thumbnailTool.fields.desiredOutcome.placeholder",
                    "Desired outcome"
                  )}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("thumbnailTool.fields.avoidPhrases.label", "Avoid Phrases")}
                  <FieldHint
                    text={t(
                      "thumbnailTool.fields.avoidPhrases.hint",
                      "Words or expressions you do not want the analyzer to repeat in its suggestions."
                    )}
                  />
                </label>
                <input
                  value={avoidPhrases}
                  onChange={(e) => setAvoidPhrases(e.target.value)}
                  placeholder={t(
                    "thumbnailTool.fields.avoidPhrases.placeholder",
                    "Avoid phrases"
                  )}
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginTop: "12px" }}>
              <label style={labelStyle}>
                {t("thumbnailTool.fields.extraContext.label", "Extra Context")}
                <FieldHint
                  text={t(
                    "thumbnailTool.fields.extraContext.hint",
                    "Add emotional target, positioning notes, competitor style, or extra context to improve the suggestions."
                  )}
                />
              </label>
              <textarea
                value={extraContext}
                onChange={(e) => setExtraContext(e.target.value)}
                placeholder={t(
                  "thumbnailTool.fields.extraContext.placeholder",
                  "Extra context, emotional target, or positioning note"
                )}
                style={textareaStyle}
              />
            </div>
          </div>

          {error && (
            <div
              style={{
                padding: "12px 14px",
                borderRadius: "12px",
                background: colors.dangerBg,
                color: colors.dangerText,
              }}
            >
              {error}
            </div>
          )}

          <ActionRow>
            <AIActionButton isDark={isDark}
              onClick={analyzeText}
              disabled={loading}
            >
              {loading
                ? t("thumbnailTool.actions.analyzing", "Analyzing...")
                : t("thumbnailTool.actions.analyze", "Analyze Thumbnail Text")}
            </AIActionButton>

            <UtilityButton isDark={isDark}
              type="button"
              onClick={clearResults}
            >
              {t("thumbnailTool.actions.clearResults", "Clear Results")}
            </UtilityButton>

            <UtilityButton isDark={isDark}
              type="button"
              onClick={resetForm}
            >
              {t("thumbnailTool.actions.resetForm", "Reset Form")}
            </UtilityButton>
          </ActionRow>
        </div>
      </div>

      {loading ? (
        <ToolResultSkeleton isDark={isDark} count={3} />
      ) : analysis && (
        <ResultCard
          colors={colors}
          index={0}
        >
          <div style={{ color: colors.text, fontWeight: 700, fontSize: "18px" }}>
            {t("thumbnailTool.analysis.title", "Current Text Analysis")}
          </div>

          <div style={{ color: colors.text, marginTop: "10px", lineHeight: "1.6" }}>
            {analysis.text}
          </div>

          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "10px" }}>
            <span
              style={{
                background: getScoreTone(analysis.score).pillBg,
                borderRadius: "999px",
                padding: "6px 10px",
                fontSize: "12px",
                color: getScoreTone(analysis.score).pillText,
                fontWeight: 700,
              }}
            >
              {t("thumbnailTool.labels.score", "Score")}: {analysis.score}
            </span>

            <span
              style={{
                background: colors.metricsBg,
                borderRadius: "999px",
                padding: "6px 10px",
                fontSize: "12px",
                color: colors.metricsText,
                fontWeight: 700,
              }}
            >
              {t("thumbnailTool.labels.words", "Words")}: {analysis?.metrics?.word_count ?? 0}
            </span>

            <span
              style={{
                background: colors.metricsBg,
                borderRadius: "999px",
                padding: "6px 10px",
                fontSize: "12px",
                color: colors.metricsText,
                fontWeight: 700,
              }}
            >
              {t("thumbnailTool.labels.characters", "Characters")}:{" "}
              {analysis?.metrics?.character_count ?? 0}
            </span>
          </div>

          {renderScoreBar(analysis.score)}

          {Array.isArray(analysis.feedback) && analysis.feedback.length > 0 && (
            <ul
              style={{
                margin: "12px 0 0",
                paddingLeft: "18px",
                color: colors.textMuted,
                lineHeight: "1.6",
              }}
            >
              {analysis.feedback.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}

          <ActionRow style={{ marginTop: "14px" }}>
            <CopyButton
              copied={copiedId === "analysis"}
              onClick={() => copyText(analysis.text, "analysis")}
            >
              {copiedId === "analysis"
                ? t("thumbnailTool.actions.copied", "Copied")
                : t("thumbnailTool.actions.copy", "Copy")}
            </CopyButton>

            <SaveStudioAssetButton
              toolType="thumbnail"
              title={`Thumbnail Text • ${sharedTopic || t("thumbnailTool.labels.untitledTopic", "Untitled Topic")}`}
              topic={sharedTopic}
              platform="YouTube"
              content={analysis.text || ""}
              meta={{
                score: analysis.score,
                label: analysis.label,
                feedback: analysis.feedback || [],
              }}
            />
          </ActionRow>
        </ResultCard>
      )}

      {!!suggestions.length && (
        <div
          style={{
            color: colors.textMuted,
            fontSize: "14px",
            lineHeight: "1.6",
            marginTop: "-4px",
          }}
        >
          {t(
            "thumbnailTool.suggestionsIntro",
            "These are suggested thumbnail text alternatives. Pick the line that feels clearest, punchiest, and most clickable for your audience."
          )}
        </div>
      )}

      <div style={{ display: "grid", gap: "14px" }}>
        {suggestions.map((item, index) => {
          const tone = getScoreTone(item.score);

          return (
            <ResultCard
              key={`${item.text}-${index}`}
              colors={colors}
              index={index}
            >
              <div style={{ color: colors.text, fontWeight: 700, fontSize: "18px", lineHeight: "1.5" }}>
                {item.text}
              </div>

              <div style={{ marginTop: "10px" }}>
                <span
                  style={{
                    background: tone.pillBg,
                    color: tone.pillText,
                    borderRadius: "999px",
                    padding: "6px 10px",
                    fontSize: "12px",
                    fontWeight: 700,
                  }}
                >
                  {t("thumbnailTool.labels.score", "Score")}: {item.score}
                </span>
              </div>

              {renderScoreBar(item.score)}

              <ActionRow style={{ marginTop: "14px" }}>
                <CopyButton
                  copied={copiedId === index}
                  onClick={() => copyText(item.text, index)}
                >
                  {copiedId === index
                    ? t("thumbnailTool.actions.copied", "Copied")
                    : t("thumbnailTool.actions.copy", "Copy")}
                </CopyButton>

                <SaveStudioAssetButton
                  toolType="thumbnail"
                  title={`Thumbnail Suggestion • ${sharedTopic || t("thumbnailTool.labels.untitledTopic", "Untitled Topic")}`}
                  topic={sharedTopic}
                  platform="YouTube"
                  content={item.text || ""}
                  meta={{ score: item.score, label: item.label }}
                />
              </ActionRow>
            </ResultCard>
          );
        })}
      </div>
    </div>
    </MotionSurface>
  );
};

export default ThumbnailTool;